import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { Fragment, useEffect, useRef, useState } from 'react';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import FormControlLabel from '@mui/material/FormControlLabel';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import { Alert, Chip, Divider, styled, useTheme } from '@mui/material';
import { useFlow } from "../context/FlowContext";
import { useSettings } from '../context/SettingsContext';
import { useI18N } from '../context/I18NContext';
import { BoxContainer } from '../layouts/Theme';
import useDataStore from '../store/useDataStore';
import useWidgetStore from '../store/useWidgetStore';
import { FormContainer, FormErrorProvider, useForm } from 'react-hook-form-mui';
import { useNavigate } from 'react-router-dom';
// alias the Cart, as we also need the Cart type
import CartComponent from '../partials/checkout/Cart';
import PollingPaymentStatus from '../partials/checkout/PollingPaymentStatus';
import ErrorAlert from '../partials/ErrorAlert';
import SuccessAlert from '../partials/SuccessAlert';
import { PaymentOptionMembershipBuy, PaymentOptionMembershipMandate, PaymentOptionMembershipUse, PaymentOptionProviderAlternate, PaymentOptionProviderAlternates, PaymentOptionProviderExternal, PaymentOptionProviderNone, PaymentOptionProviderRemote, PaymentOptionSubscriptionBuy, PaymentOptionSubscriptionUse } from './Payment/PaymentOptions';
import { useUpdateCart } from '../hooks/query/useUpdateCart';
import { useAbort } from '../hooks/query/useAbort';
import { createRow, moneyFormat, subtotal } from '../helpers/general';
import ConfigHolder from '../config/ConfigHolder';
export default function Payment(props) {
    var _a;
    const flow = useFlow();
    const theme = useTheme();
    const navigate = useNavigate();
    const formContext = useForm({});
    const [error, setError] = useState();
    const [paymentError, setPaymentError] = useState();
    const [redirectUri, setRedirectUri] = useState('');
    const [submitLabel, setSubmitLabel] = useState('Betaling afronden');
    const [subTotal, setSubTotal] = useState(0);
    const [paymentMethod, setPaymentMethod] = useState();
    // the payment method we are displaying. This does not set the actual paymentMethod which triggers a lot of stuff
    const [displayPaymentMethod, setDisplayPaymentMethod] = useState();
    const [paymentOption, setPaymentOption] = useState("full");
    const [submitMode, setSubmitMode] = useState("submit");
    const [errorValidate, setErrorValidate] = useState();
    const [successValidate, setSuccessValidate] = useState();
    // has memberships or subscriptions that we can either buy or use
    const [hasValidAlternates, setHasValidAlternates] = useState(false);
    const [hasValidUsables, setHasValidUsables] = useState(false);
    const [hasValidMoreAlternates, setHasValidMoreAlternates] = useState(false);
    // has Mollie methods, like ideal, klarna etc
    const [hasValidExternals, setHasValidExternals] = useState(false);
    // show the list of alternates we can use
    const [showAlternates, setShowAlternates] = useState(false);
    // show the list of Mollie methods
    const [showExternals, setShowExternals] = useState(false);
    // show the rest of the alternate we can buy
    const [showMoreAlternates, setShowMoreAlternates] = useState(false);
    const [useDeposit, setUseDeposit] = useState(false);
    const [canDeposit, setCanDeposit] = useState(false);
    const [depositPrice, setDepositPrice] = useState(0);
    const [cartFees, setCartFees] = useState();
    const [loader, setLoader] = useState(false);
    const [enableSubmit, setEnableSubmit] = useState(false);
    const [isFinal, setIsFinal] = useState(false);
    // const [isPaying, setIsPaying] = useState<boolean>(false)
    const [requiresAdditionalPayment, setRequiresAdditionalPayment] = useState(false);
    const [invoiceTotal, setInvoiceTotal] = useState(0);
    const [hasAppointmentProducts, setHasAppointmentProducts] = useState(false);
    const { cart, setCart, consumer, booking, paymentMethods, setPaymentMethods, paymentOptions, setPaymentOptions, fees, setFees, isPaying, setIsPaying, getDaoAppointments, selectedCapacity, selectedApptype, selectedResource } = useWidgetStore();
    const { apptypes, resources, products, } = useDataStore();
    const { api_key, widget_id, payment, hasCheckout, widgetMode } = useSettings();
    const { t, ts } = useI18N();
    const formContextUpdateCart = useForm({});
    const updateCartQuery = useUpdateCart(formContextUpdateCart);
    const abortQuery = useAbort();
    // While an abort is in flight the order it cancels still exists server side, so every
    // updateCart fired in that window races it and comes back complaining about the time slot.
    // Those answers overwrite the error that started the abort in the first place. A ref rather
    // than abortQuery.isFetching, because the gate has to close in the same tick as the refetch
    // that opens it, not on the render after.
    const abortInFlight = useRef(false);
    const startAbort = () => {
        if (abortInFlight.current) {
            return;
        }
        abortInFlight.current = true;
        abortQuery.refetch();
    };
    const refetchCart = () => {
        if (abortInFlight.current) {
            return;
        }
        updateCartQuery.refetch();
    };
    // A payment option the consumer can see and has actually checked. `paymentMethod` and
    // `displayPaymentMethod` are not that on their own: 'alternate' and 'external' only say which
    // list we are showing, and a method left over from an earlier pass through this view (going
    // back, changing the appointment type) belongs to a radio that is no longer rendered.
    const hasCheckedPaymentMethod = () => document.querySelectorAll('.Mui-checked input[name=paymentMethod]').length > 0;
    // Whether there is anything to pick at all. An order with nothing left to pay renders no
    // options, and there the button only confirms.
    const hasPaymentMethodOptions = () => document.querySelectorAll('input[name=paymentMethod]').length > 0;
    useEffect(() => {
        setHasAppointmentProducts(cart.lines.filter((line) => line.origin === 'appointment').length > 0);
    }, [cart.lines]);
    useEffect(() => {
        if (hasAppointmentProducts) {
            setTimeout(() => {
                if (showExternals && subTotal === 0) {
                    setEnableSubmit(true);
                }
                else {
                    setEnableSubmit(hasCheckedPaymentMethod());
                    if (!hasCheckedPaymentMethod() && displayPaymentMethod !== undefined) {
                        // if we have no payment method checked, but there still is a selection, we got here via alternate flows ( f.e., edit the appointment while already into the payment proces)
                        // and since we can only catch the initial render once, we reset it here
                        // this is a bit hacky, a proper fix would rewrite the cart summary component to somehow "talk" to the view it's in ( as it's a generic component, it can't right now )
                        setDisplayPaymentMethod(undefined);
                        setPaymentMethod(undefined);
                    }
                }
            }, 500);
        }
        else {
            // no appointments
            // check if the payment method selected is actually valid. To be precise: if the consumer has a subscription or membership
            // this will be pre-filled elsewhere
            if ((paymentMethod === null || paymentMethod === void 0 ? void 0 : paymentMethod.startsWith('subscription_')) || (paymentMethod === null || paymentMethod === void 0 ? void 0 : paymentMethod.startsWith('membership_'))) {
                if (!paymentMethods.includes('alternate')) {
                    if (paymentMethods.includes('external')) {
                        setPaymentMethod('external');
                        setDisplayPaymentMethod('external');
                    }
                    else {
                        setPaymentMethod(paymentMethods[0]); // always 'none'?
                        setDisplayPaymentMethod(paymentMethods[0]);
                    }
                }
            }
            else if (paymentMethod !== 'external') {
                setEnableSubmit(true);
            }
            else {
                setEnableSubmit(false);
                setSubmitLabel('Afrekenen');
            }
        }
    }, [displayPaymentMethod, paymentMethod, submitMode, loader, hasAppointmentProducts]);
    useEffect(() => {
        if (error && error.length > 0) {
            setFees([]);
            setCartFees([]);
            refetchCart();
        }
    }, [error]);
    // this monitors the polling status, and goes to the next step when the payment is finalized
    useEffect(() => {
        var _a, _b, _c;
        if (cart.currentPayment) {
            let failed = true;
            if (cart.currentPayment.status === "success" && ((_b = (_a = cart.currentPayment) === null || _a === void 0 ? void 0 : _a.transaction) === null || _b === void 0 ? void 0 : _b.final)) {
                failed = false;
                setIsPaying(false);
                setIsFinal(false);
                if (flow.onNext) {
                    flow.onNext();
                }
            }
            else if (cart.currentPayment.status === "open") {
                failed = false;
            }
            else if (cart.currentPayment.status === "failed") {
                setPaymentError([t("Er is iets misgegaan bij het betalen. Probeer het later opnieuw.", "payment")]);
            }
            else if (cart.currentPayment.status === "cancelled") {
                setPaymentError([t("Je hebt de betaling geannuleerd", "payment")]);
            }
            else if (cart.currentPayment.status === "expired") {
                setPaymentError([t("De betaling is verlopen. Probeer het later opnieuw.", "payment")]);
            }
            else {
                setPaymentError([t("Er is iets misgegaan bij het betalen. Probeer het later opnieuw.", "payment")]);
            }
            if (failed) {
                setIsPaying(false);
                setIsFinal(false);
                // The subscription or membership we tried to buy is a line in the cart, and getDaoProducts
                // turns every non-appointment line into a product on the next updateCart. The backend reads
                // that as a second one to buy and puts it alongside the one it already adds for the
                // appointment, so the cart doubles. Neither can pay for anything but an appointment, so when
                // one of them is the payment method there is nothing else in the cart worth keeping — reset
                // the products.
                let payingWith = formContextUpdateCart.getValues('paymentMethod');
                setCart(Object.assign(Object.assign({}, cart), { 
                    // the method the update will actually be sent with, not the `paymentMethod` state:
                    // picking iDEAL to pay for the strippenkaart leaves that state on 'ideal' while the
                    // update still goes out as subscription_51
                    lines: (payingWith === null || payingWith === void 0 ? void 0 : payingWith.startsWith('subscription')) || (payingWith === null || payingWith === void 0 ? void 0 : payingWith.startsWith('membership'))
                        ? cart.lines.filter((line) => line.productType === 'appointment')
                        : cart.lines, currentPayment: undefined }));
                setDisplayPaymentMethod(undefined);
                let radio = document.querySelector(`input[name=paymentMethod][value=${displayPaymentMethod}]`);
                if (radio) {
                    //@ts-ignore
                    (_c = radio.parentNode) === null || _c === void 0 ? void 0 : _c.click();
                }
                if (!updateCartQuery.isRefetching) {
                    refetchCart();
                }
            }
        }
    }, [(_a = cart === null || cart === void 0 ? void 0 : cart.currentPayment) === null || _a === void 0 ? void 0 : _a.status]);
    // paymentmethods is set by the summary, but could happen anywhere, so we listen to it here and update our options
    // accordingly
    useEffect(() => {
        var _a;
        if (paymentMethods.length > 0) {
            setHasValidAlternates(paymentMethods.includes("alternate"));
            setHasValidExternals(paymentMethods.includes("external")); //|| paymentMethods.includes('none'))
            if (paymentMethods.includes("alternate") && consumer.subscriptions) {
                // if we have memberships or subscriptions to use/buy, start on that list
                let usables = (_a = consumer === null || consumer === void 0 ? void 0 : consumer.subscriptions) === null || _a === void 0 ? void 0 : _a.filter((subscription) => !!subscription.subscriptionId);
                setDisplayPaymentMethod("alternate");
                setHasValidUsables(usables.length > 0);
                if (usables.length === 0) {
                    // show only the subscriptions we can  buy
                    setHasValidMoreAlternates(true);
                    setShowMoreAlternates(true);
                    setShowAlternates(false);
                }
                else if (consumer.subscriptions.length > usables.length) {
                    // show the subscriptions we can  use,. and there are others we can buy
                    setShowAlternates(true);
                    setHasValidMoreAlternates(true);
                }
                else {
                    // we  can only use
                    setShowAlternates(true);
                    setHasValidMoreAlternates(false);
                }
            }
            else if (paymentMethods.includes("alternate") && !consumer.subscriptions) {
                // be can only buy
                setShowMoreAlternates(true);
                setHasValidMoreAlternates(true);
            }
            else if (paymentMethods.includes("external")) {
                // if we have other Mollie methods, start on that list
                setDisplayPaymentMethod("external");
                setShowExternals(true);
            }
            else {
                // we have eiher 'none' or no methods at all, so this is only a confirm button
            }
        }
    }, [paymentMethods, consumer.subscriptions]);
    useEffect(() => {
        // @ts-ignore
        setDepositPrice(paymentOptions.deposit && paymentOptions.deposit > 0 ? paymentOptions.deposit / 100 : 0);
        // @ts-ignore
        setCanDeposit(!!(paymentOptions.deposit && paymentOptions.deposit > 0));
    }, [paymentOptions]);
    useEffect(() => {
        var _a, _b, _c, _d, _e, _f;
        if (updateCartQuery.data) {
            setLoader(false);
            let response = updateCartQuery.data;
            if (response.status.status === 'success') {
                let result = response.result.item;
                // we check these together as we only need 1 navigate to thankyou
                if ((result === null || result === void 0 ? void 0 : result.redirect) && ((_a = result === null || result === void 0 ? void 0 : result.redirect) === null || _a === void 0 ? void 0 : _a.uri)) {
                    if (result.redirect.uri === '#oa_thankyou') {
                        setIsPaying(false);
                        setIsFinal(false);
                        if (flow.onNext) {
                            flow.onNext();
                        }
                    }
                    else {
                        if (widgetMode === 'standalone') {
                            window.location.href = (_b = result === null || result === void 0 ? void 0 : result.redirect) === null || _b === void 0 ? void 0 : _b.uri;
                        }
                        else {
                            window.parent.postMessage({
                                type: 'redirect',
                                url: (_c = result === null || result === void 0 ? void 0 : result.redirect) === null || _c === void 0 ? void 0 : _c.uri
                            }, '*');
                        }
                    }
                }
                else {
                    setIsPaying(false);
                    setIsFinal(false);
                    let _paymentOptions = result.paymentOptions;
                    setPaymentOptions(_paymentOptions);
                    // @ts-ignore
                    setFees(result.fees);
                    // @ts-ignore
                    setDepositPrice(_paymentOptions.deposit && _paymentOptions.deposit > 0 ? _paymentOptions.deposit / 100 : 0);
                    // @ts-ignore
                    setCanDeposit(!!(_paymentOptions.deposit && _paymentOptions.deposit > 0));
                    // these are fetched once on the summary in the prepare API
                    //setPaymentMethods(result.paymentMethods)
                    if (result.order.order_id) {
                        setCart(Object.assign(Object.assign({}, cart), { 
                            // the order lines the backend just built: the appointment plus whatever the
                            // consumer chose to buy. They carry no `origin`, which the views downstream
                            // read (hasAppointmentProducts, the optionalPurchase rows), so keep the origin
                            // of the line we already had and fall back to the product type for new ones.
                            lines: result.lines.map((line) => {
                                var _a, _b;
                                return (Object.assign(Object.assign({}, line), { origin: (_b = (_a = cart.lines.find((existing) => existing.productId === line.productId)) === null || _a === void 0 ? void 0 : _a.origin) !== null && _b !== void 0 ? _b : (line.productType === 'appointment' ? 'appointment' : 'optionalPurchase') }));
                            }), giftCards: result.giftCards, 
                            // discounts: result.discounts, we keep track of the applied discounts locally
                            order_id: result.order.order_id, token: result.csrfToken }));
                        let subscriptionPayment = result.lines.find((line) => line.productType === "subscription");
                        if (subscriptionPayment) {
                            let _externalMethods = result.paymentMethods;
                            _externalMethods.push("external");
                            setPaymentMethods(_externalMethods);
                            setShowExternals(true);
                            setShowAlternates(false);
                            setShowMoreAlternates(false);
                            setHasValidExternals(true);
                            setDisplayPaymentMethod("external");
                        }
                        if (showAlternates && hasValidMoreAlternates && displayPaymentMethod === "alternate") {
                            setShowAlternates(false);
                            setShowMoreAlternates(true);
                        }
                    }
                }
            }
            else if (response.status.status === 'failed') {
                if (!((_d = cart.order_id) === null || _d === void 0 ? void 0 : _d.startsWith('tmp_')) || response.status.userMessages.includes("Abonnement of Strippenkaart niet geldig.")) {
                    setError((_e = response.status.userMessages) !== null && _e !== void 0 ? _e : []);
                }
                setIsPaying(false);
                setIsFinal(false);
                formContextUpdateCart.setValue("final", false);
                if (formContextUpdateCart.getValues("selectedSubscription")) {
                    formContextUpdateCart.setValue("selectedSubscription", undefined);
                    if ((_f = formContext.getValues('paymentMethod')) === null || _f === void 0 ? void 0 : _f.startsWith('membership_')) {
                        formContextUpdateCart.setValue("paymentMethod", "alternate");
                    }
                }
                //if (response.status.userMessages.includes("Abonnement of Strippenkaart niet geldig.")) {
                startAbort();
                //}
                //else {
                //    refetchCart()
                //}
            }
        }
    }, [updateCartQuery.data]);
    useEffect(() => {
        setCartFees([]);
        if (displayPaymentMethod) {
            if (fees[displayPaymentMethod]) {
                let fee = fees[displayPaymentMethod];
                if (useDeposit && fee.deposit && fee.deposit > 0) {
                    setCartFees([{
                            name: t('Transactiekosten'),
                            value: fee.deposit / 100
                        }]);
                }
                else if (fee.full && fee.full > 0) {
                    setCartFees([{
                            name: t('Transactiekosten'),
                            value: fee.full / 100
                        }]);
                }
            }
            if (displayPaymentMethod === 'none' || (displayPaymentMethod === null || displayPaymentMethod === void 0 ? void 0 : displayPaymentMethod.startsWith('membership_')) || (displayPaymentMethod === null || displayPaymentMethod === void 0 ? void 0 : displayPaymentMethod.startsWith('subscription_'))) {
                setCanDeposit(false);
                setUseDeposit(!paymentOptions.full && !!(paymentOptions.deposit && paymentOptions.deposit > 0));
            }
            else {
                setCanDeposit(!!(paymentOptions.deposit && paymentOptions.deposit > 0));
            }
            setPaymentOption(useDeposit ? 'deposit' : 'full');
        }
    }, [paymentMethod, displayPaymentMethod, useDeposit]);
    useEffect(() => {
        if (cart.mandatables && cart.mandatables[0] && cart.mandatables[0].mandatable) {
            if (flow.onNext) {
                flow.onNext();
            }
        }
    }, [cart.mandatables]);
    useEffect(() => {
        var _a, _b, _c, _d;
        let _rows = [];
        if (booking && apptypes && ((_a = booking === null || booking === void 0 ? void 0 : booking.appointments) !== null && _a !== void 0 ? _a : []).length > 0) {
            let appointment = booking.appointments[0];
            if (appointment) {
                let apptypeId = (_c = (_b = booking === null || booking === void 0 ? void 0 : booking.appointments[0]) === null || _b === void 0 ? void 0 : _b.components[0]) === null || _c === void 0 ? void 0 : _c.appointmentTypes[0];
                let apptype = apptypes.find((apptype) => apptype.id === apptypeId);
                if (apptype) {
                    _rows.push(createRow([], ((_d = appointment === null || appointment === void 0 ? void 0 : appointment.price) !== null && _d !== void 0 ? _d : 0), // this is the price set by the bookable time, which can be a sum of the components
                    1, 0, //   21,
                    "appointment", "appointment"));
                }
            }
        }
        // let _subtotal: number = subtotal(_rows)
        // setSubTotal(_subtotal)
    }, [apptypes, resources, products, booking, cart]);
    useEffect(() => {
        var _a;
        // this effect tries to preselect the first usable alternate ( subscription / membership )
        if (paymentMethods.length > 0) {
            if ((consumer === null || consumer === void 0 ? void 0 : consumer.subscriptions) && !cart.lines.find((line) => ['subscription', 'membership'].includes(line.productType))) {
                let usable = consumer.subscriptions.find((subscription) => subscription.subscriptionId);
                if (usable && !paymentMethod) {
                    setDisplayPaymentMethod(`${usable.type}_${usable.id}`);
                    setPaymentMethod(`${usable.type}_${usable.id}`);
                    formContext.setValue('paymentMethod', `${usable.type}_${usable.id}`);
                    let radio = document.querySelector(`input[name=paymentMethod][value=${usable.type}_${usable.id}]`);
                    if (radio) {
                        //@ts-ignore
                        (_a = radio.parentNode) === null || _a === void 0 ? void 0 : _a.click();
                    }
                }
                if (!usable && hasValidMoreAlternates) {
                    setShowMoreAlternates(true);
                }
            }
        }
    }, [paymentMethods, paymentMethod, displayPaymentMethod]);
    const onSubmit = (data, event) => {
        var _a, _b;
        let _final = false;
        formContextUpdateCart.setValue('paymentMethod', data.paymentMethod);
        formContextUpdateCart.setValue('paymentOption', paymentOption);
        localStorage.setItem('storedCart', JSON.stringify(cart));
        localStorage.setItem('storedBooking', JSON.stringify(booking));
        if (data.paymentMethod.startsWith('subscription')) {
            let subscription = (_a = consumer.subscriptions) === null || _a === void 0 ? void 0 : _a.find((subscription) => subscription.id === parseInt(data.paymentMethod.substring(13)));
            if (subscription === null || subscription === void 0 ? void 0 : subscription.subscriptionId) {
                subscription.active = true;
                _final = true;
                formContextUpdateCart.setValue('selectedSubscription', subscription);
            }
            else if (subscription === null || subscription === void 0 ? void 0 : subscription.id) {
                subscription.active = false;
                _final = false;
                formContextUpdateCart.setValue('selectedSubscription', subscription);
            }
        }
        else if (data.paymentMethod.startsWith('membership')) {
            let pm = data.paymentMethod.substring(11);
            if (pm.endsWith('mandate')) {
                pm = pm.substring(0, pm.indexOf('_mandate'));
            }
            let subscription = (_b = consumer.subscriptions) === null || _b === void 0 ? void 0 : _b.find((subscription) => subscription.id === parseInt(pm));
            if (subscription === null || subscription === void 0 ? void 0 : subscription.subscriptionId) {
                // use an existing membership
                subscription.active = true;
                _final = true;
                formContextUpdateCart.setValue('selectedSubscription', subscription);
            }
            else {
                if (subscription === null || subscription === void 0 ? void 0 : subscription.mandatable) {
                    if (subscription) {
                        _final = false;
                        // add the selected subscription to mandatables
                        // this will trigger the flow context to navigate to the mandate form
                        setCart(Object.assign(Object.assign({}, cart), { mandatables: [subscription] }));
                    }
                }
                else {
                    // if (data.paymentMethod.endsWith('mandate')) {
                    //     console.log('buy new membership with mandate', subscription)
                    //     if (subscription) {
                    //         setCart({ ...cart, mandatables: [subscription] })
                    //     }
                    // }
                    // else 
                    if (subscription === null || subscription === void 0 ? void 0 : subscription.id) {
                        // buy a new membership, there is no option to mandate
                        subscription.active = false;
                        _final = false;
                        formContextUpdateCart.setValue('selectedSubscription', subscription);
                    }
                }
            }
        }
        else {
            _final = true;
            if (flow) {
                flow === null || flow === void 0 ? void 0 : flow.setBackCallback(undefined);
            }
        }
        _final = _final && submitMode === "submit";
        formContextUpdateCart.setValue('final', _final);
        setIsFinal(_final);
        if (_final) {
            if (!["none", "alternate"].includes(displayPaymentMethod) && !(displayPaymentMethod === null || displayPaymentMethod === void 0 ? void 0 : displayPaymentMethod.startsWith('membership')) && !(displayPaymentMethod === null || displayPaymentMethod === void 0 ? void 0 : displayPaymentMethod.startsWith('subscription'))) {
                // dont show the polling for none,  membership and subscriptions
                setIsPaying(true);
            }
        }
        else {
            // just refetch the new cart, without the payment loader
            // this also means updating the payment methods
            refetchCart();
        }
    };
    useEffect(() => {
        if (isFinal) {
            if (subTotal === 0 && paymentMethod === "none" && displayPaymentMethod !== "none") {
                setDisplayPaymentMethod("none");
                formContext.setValue('paymentMethod', "none");
                setTimeout(() => {
                    refetchCart();
                }, 500);
            }
            else {
                refetchCart();
            }
        }
    }, [isFinal]);
    // useEffect(() => {
    //     if (subTotal === 0 && paymentMethod === "none" && displayPaymentMethod !== "none") {
    //         formContextUpdateCart.setValue('final', true)
    //         setIsFinal(true)
    //         setPaymentMethod("none")
    //     }
    // }, [subTotal, paymentMethod, displayPaymentMethod])
    const submitUrl = ConfigHolder.getApiMethodUrl(api_key, widget_id, 'updateCart', 'checkout');
    // prepare the POST to  a new tab where we redirect to Mollie
    const prepareSubmit = (event) => {
        // prepare alternate membership_55
        // true
        // console.log(submitMode, displayPaymentMethod, paymentMethod)
        // console.log(submitMode === "submit" && (
        //     ["none", "alternate"].includes(displayPaymentMethod as PaymentMethodType)) ||
        //     displayPaymentMethod?.startsWith('membership') ||
        //     displayPaymentMethod?.startsWith('subscription') ||
        //     subTotal === 0)
        // a yes, brackets
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
        if (submitMode === "submit" && ((["none", "alternate"].includes(displayPaymentMethod)) ||
            (displayPaymentMethod === null || displayPaymentMethod === void 0 ? void 0 : displayPaymentMethod.startsWith('membership')) ||
            (displayPaymentMethod === null || displayPaymentMethod === void 0 ? void 0 : displayPaymentMethod.startsWith('subscription')) ||
            subTotal === 0)) {
            // if we are actually paying and we use a membership, subscription or none, don't post
            // to a new tab, but trigger the submit on the context form instead
            setPaymentMethod(displayPaymentMethod);
            onSubmit({ paymentMethod: displayPaymentMethod }, event);
            // return false to stop the form from submitting
            event.preventDefault();
            return false;
        }
        if (submitMode === "prepare") {
            if (displayPaymentMethod === "alternate") {
                setShowAlternates(false);
                setShowExternals(false);
                setShowMoreAlternates(true);
            }
            else if (displayPaymentMethod === "external") {
                setShowAlternates(false);
                setShowMoreAlternates(false);
                setShowExternals(true);
            }
            else {
                // console.log(submitMode, displayPaymentMethod)
            }
            event.preventDefault();
            return false;
        }
        // // this gets posted into a new tab
        let _appointments = getDaoAppointments(), _products = [], //getDaoProducts(),
        selectedSubscription = formContextUpdateCart.getValues('selectedSubscription'), _convertedAppointments = _appointments.length > 0
            ? [{
                    appTypeId: selectedApptype, // booking?.appointments[0]?.components[0]?.appointmentTypes,
                    resourceId: selectedResource,
                    capacity: selectedCapacity,
                    components: _appointments,
                    date: (_a = _appointments[0].date) !== null && _a !== void 0 ? _a : _appointments[0].time,
                    time: _appointments[0].time,
                    fields: (_b = consumer.fields) !== null && _b !== void 0 ? _b : {},
                    remark: (_c = booking.remark) !== null && _c !== void 0 ? _c : ''
                }]
            : [];
        let data = {
            order_id: (_d = cart.order_id) !== null && _d !== void 0 ? _d : '',
            giftCards: cart.giftCards,
            discounts: cart.discounts,
            appointments: _convertedAppointments,
            products: _products,
            paymentMethod: displayPaymentMethod,
            paymentOption: paymentOption,
            final: true,
            widgetUrl: window.location.href,
            subscriptionId: (_e = selectedSubscription === null || selectedSubscription === void 0 ? void 0 : selectedSubscription.id) !== null && _e !== void 0 ? _e : undefined
        };
        const payload = document.getElementById('payload');
        if (payload) {
            payload.value = JSON.stringify(data);
        }
        const csrfToken = document.getElementById('csrfToken');
        if (csrfToken) {
            csrfToken.name = (_g = (_f = cart.token) === null || _f === void 0 ? void 0 : _f.name) !== null && _g !== void 0 ? _g : "";
            csrfToken.value = (_j = (_h = cart.token) === null || _h === void 0 ? void 0 : _h.token) !== null && _j !== void 0 ? _j : "";
        }
        const secureToken = document.getElementById('secureToken');
        if (secureToken) {
            secureToken.value = (_k = cart.secureToken) !== null && _k !== void 0 ? _k : "";
        }
        // for debugging, I can select trustly to console log the data, instead of posting to a new tab
        // because it's a new tab, we also start without the developer toolbar, and the tab redirect to Mollie
        // so we can never repost in that tab to see what was sent
        if (data.paymentMethod === "trustly") {
            console.log(data);
            event.preventDefault();
            return false;
        }
        else {
            setTimeout(() => {
                // this is a tiny bit of delay, because otherwise we would rerender instantly, causing the actual
                // POST to never proceed
                setIsPaying(true);
            }, 250);
        }
    };
    useEffect(() => {
        if (!abortQuery.data) {
            return;
        }
        abortInFlight.current = false;
        let currentRoute = location.hash.substring(12);
        if (cart.order_id && currentRoute === "payment") {
            let productLines = cart.lines.filter((line) => line.origin !== 'appointment');
            setCart(Object.assign(Object.assign({}, cart), { order_id: undefined, lines: productLines }));
            setDisplayPaymentMethod(undefined);
            setPaymentMethod(undefined);
            refetchCart();
        }
    }, [abortQuery.data]);
    useEffect(() => {
        // an abort that fails would otherwise leave the gate shut for good
        if (abortQuery.isError) {
            abortInFlight.current = false;
        }
    }, [abortQuery.isError]);
    const backCallback = () => {
        var _a;
        if (flow) {
            flow === null || flow === void 0 ? void 0 : flow.setBackCallback(undefined);
            let currentRoute = location.hash.substring(12);
            if (((_a = flow.currentRoute) === null || _a === void 0 ? void 0 : _a.route.startsWith("mandate")) || currentRoute.startsWith("mandate")) {
                if (!abortQuery.isRefetching) {
                    startAbort();
                }
            }
            else {
                let _showAlternates = hasValidAlternates && hasValidUsables, _showMoreAlternates = hasValidMoreAlternates && !hasValidUsables;
                setShowAlternates(_showAlternates);
                setShowMoreAlternates(_showMoreAlternates);
                setShowExternals(hasValidExternals && !_showAlternates && !_showMoreAlternates);
                setIsFinal(false);
                setIsPaying(false);
                setLoader(false);
                setPaymentMethod(undefined);
                setDisplayPaymentMethod(hasValidAlternates ? "alternate" : (hasValidExternals ? "external" : undefined));
                if (showAlternates && formContextUpdateCart.getValues('selectedSubscription')) {
                    // remove the subscription we selected, and start fresh
                    formContextUpdateCart.setValue("selectedSubscription", undefined);
                }
                refetchCart();
            }
        }
    };
    useEffect(() => {
        if (requiresAdditionalPayment && !(displayPaymentMethod === null || displayPaymentMethod === void 0 ? void 0 : displayPaymentMethod.endsWith('addSubscription')) && !(displayPaymentMethod === null || displayPaymentMethod === void 0 ? void 0 : displayPaymentMethod.endsWith('addPayment'))) {
            setEnableSubmit(false);
        }
        else {
            // no longer requiring an additional payment is not a choice the consumer made, so as
            // long as there are options on screen one of them has to be checked before the button
            // is armed.
            setEnableSubmit(!hasPaymentMethodOptions() || hasCheckedPaymentMethod());
        }
        // the options on screen change with the list we are showing and with the cart being
        // refetched, so re-check whenever any of that moves
    }, [requiresAdditionalPayment, displayPaymentMethod, paymentMethods, showAlternates, showMoreAlternates, showExternals, subTotal, loader, updateCartQuery.isFetching]);
    const onChange = (event) => {
        var _a, _b, _c;
        let newPaymentMethod = event.currentTarget.value;
        formContext.setValue('paymentMethod', newPaymentMethod);
        setDisplayPaymentMethod(newPaymentMethod);
        if ((newPaymentMethod === null || newPaymentMethod === void 0 ? void 0 : newPaymentMethod.startsWith('subscription')) || (newPaymentMethod === null || newPaymentMethod === void 0 ? void 0 : newPaymentMethod.startsWith('membership'))) {
            setPaymentMethod(newPaymentMethod);
            if (newPaymentMethod === null || newPaymentMethod === void 0 ? void 0 : newPaymentMethod.startsWith('subscription')) {
                let subscriptionId = parseInt(newPaymentMethod.substring(13)), subscription = (_a = consumer === null || consumer === void 0 ? void 0 : consumer.subscriptions) === null || _a === void 0 ? void 0 : _a.find((subscription) => subscription.id === subscriptionId), usable = !!(subscription === null || subscription === void 0 ? void 0 : subscription.subscriptionId), requiredSubscriptions = 1;
                if (!usable) {
                    if (subscription) {
                        let requiredCredits = booking.appointments.length * subscription.charge;
                        requiredSubscriptions = Math.ceil(requiredCredits / subscription.max);
                    }
                }
                setSubmitMode("submit");
                setSubmitLabel(usable
                    ? requiresAdditionalPayment
                        ? newPaymentMethod.endsWith('addSubscription')
                            ? 'Strippenkaart gebruiken en aanvullen'
                            : 'Strippenkaart gebruiken en restant afrekenen'
                        : 'Strippenkaart gebruiken'
                    : requiredSubscriptions > 1
                        ? 'Strippenkaarten aanschaffen'
                        : 'Strippenkaart aanschaffen');
                if (requiresAdditionalPayment && !newPaymentMethod.endsWith('addSubscription') && !newPaymentMethod.endsWith('addPayment')) {
                    setEnableSubmit(false);
                }
            }
            else if (newPaymentMethod === null || newPaymentMethod === void 0 ? void 0 : newPaymentMethod.startsWith('membership')) {
                let subscriptionId = parseInt(newPaymentMethod.substring(11));
                let usable = (_b = consumer === null || consumer === void 0 ? void 0 : consumer.subscriptions) === null || _b === void 0 ? void 0 : _b.find((subscription) => subscription.id === subscriptionId && subscription.subscriptionId);
                if (usable) {
                    setSubmitLabel('Abonnement gebruiken');
                }
                else {
                    let membership = (_c = consumer === null || consumer === void 0 ? void 0 : consumer.subscriptions) === null || _c === void 0 ? void 0 : _c.find((subscription) => subscription.id === subscriptionId);
                    setSubmitLabel((membership === null || membership === void 0 ? void 0 : membership.mandatable) ? 'Volgende stap' : 'Abonnement aanschaffen');
                }
                setSubmitMode("submit");
            }
            else {
                setSubmitMode("prepare");
                setSubmitLabel('Volgende stap');
            }
        }
        else if (hasValidAlternates) {
            if (newPaymentMethod === 'none') {
                setSubmitMode("submit");
                setSubmitLabel('Afspraak bevestigen');
            }
            else if (newPaymentMethod === 'alternate') {
                if (flow) {
                    flow === null || flow === void 0 ? void 0 : flow.setBackCallback(backCallback);
                }
                setSubmitMode("prepare");
                setSubmitLabel('Volgende stap');
            }
            else if (newPaymentMethod === 'external') {
                if (flow) {
                    flow === null || flow === void 0 ? void 0 : flow.setBackCallback(backCallback);
                }
                setSubmitMode("prepare");
                setSubmitLabel('Volgende stap');
            }
            else {
                setSubmitMode("submit");
                setSubmitLabel(hasAppointmentProducts ? 'Afspraak afrekenen' : 'Afrekenen');
            }
        }
        else {
            // there are no subscriptions or memberships
            setSubmitMode("submit");
            let hasSubscription = cart.lines.find((line) => {
                var _a;
                if (line.productType === "subscription" || line.productType === "membership") {
                    let lineProduct = (_a = consumer === null || consumer === void 0 ? void 0 : consumer.subscriptions) === null || _a === void 0 ? void 0 : _a.find((subscription) => {
                        return subscription.product_id == line.productId;
                    });
                    if (lineProduct)
                        return lineProduct.type === "subscription";
                }
                return false;
            });
            let hasMembership = cart.lines.find((line) => {
                var _a;
                if (line.productType === "subscription" || line.productType === "membership") {
                    let lineProduct = (_a = consumer === null || consumer === void 0 ? void 0 : consumer.subscriptions) === null || _a === void 0 ? void 0 : _a.find((subscription) => {
                        return subscription.product_id == line.productId;
                    });
                    if (lineProduct)
                        return lineProduct.type === "membership";
                }
                return false;
            });
            let label = "Afrekenen";
            if (subTotal > 0) {
                if (hasAppointmentProducts) {
                    if (hasSubscription) {
                        label = 'Strippenkaart aanschaffen';
                    }
                    else if (hasMembership) {
                        label = 'Abonnement aanschaffen';
                    }
                    else {
                        label = 'Afspraak afrekenen';
                    }
                }
                else {
                    label = 'Afrekenen';
                }
            }
            else {
                label = 'Afspraak bevestigen';
            }
            setSubmitLabel(label);
            setPaymentMethod(subTotal > 0
                ? newPaymentMethod
                : "none");
        }
    };
    useEffect(() => {
        if (subTotal === 0 && showExternals) {
            setSubmitMode("submit");
            setSubmitLabel(hasAppointmentProducts ? 'Afspraak bevestigen' : 'Bevestigen');
            // todo: figure out how the old widget deals with this
            setPaymentMethod("none");
            setEnableSubmit(true);
        }
    }, [subTotal]);
    const onError = (errors) => {
    };
    const StyledTableCell = styled(TableCell)(({ theme }) => ({
        verticalAlign: 'top',
        textAlign: 'right',
        minWidth: '4em'
    }));
    const PaymentOption = ({ subscription, provider, variant }) => {
        if (provider) {
            if (provider.key === 'external') {
                return _jsx(PaymentOptionProviderExternal, { provider: provider, useDeposit: useDeposit, fees: fees });
            }
            else if (provider.key === 'alternate') {
                return _jsx(PaymentOptionProviderAlternate, { provider: provider, useDeposit: useDeposit, fees: fees });
            }
            else if (provider.key === 'none') {
                return _jsx(PaymentOptionProviderNone, { provider: provider, useDeposit: useDeposit, fees: fees });
            }
            else if (showExternals) {
                return _jsx(PaymentOptionProviderRemote, { provider: provider, useDeposit: useDeposit, fees: fees });
            }
            else if (showAlternates) {
                return _jsx(PaymentOptionProviderAlternates, { provider: provider, useDeposit: useDeposit, fees: fees });
            }
            else {
                return _jsx(_Fragment, { children: provider.key });
            }
        }
        if (subscription) {
            if (subscription.subscriptionId) {
                if (showMoreAlternates || showExternals) {
                    return _jsx(_Fragment, {});
                }
                // we own this
                if (subscription.type === 'membership') {
                    return _jsx(PaymentOptionMembershipUse, { subscription: subscription });
                }
                else if (subscription.type === 'subscription') {
                    if (requiresAdditionalPayment) {
                        let requires = booking.appointments.length * subscription.charge, requiredCredits = requires - subscription.balance, requiredSubscriptions = Math.ceil(requiredCredits / subscription.max);
                        return variant === "addPayment"
                            ? _jsx(PaymentOptionSubscriptionUse, { subscription: subscription, addPayment: true, invoiceTotal: invoiceTotal })
                            : _jsx(PaymentOptionSubscriptionUse, { subscription: subscription, addSubscription: true, invoiceTotal: subscription.sellPrice * requiredSubscriptions, addSubscriptions: requiredSubscriptions });
                    }
                    else {
                        return _jsx(PaymentOptionSubscriptionUse, { subscription: subscription });
                    }
                }
            }
            else if (showMoreAlternates) {
                // we don't own this
                let requiredCredits = booking.appointments.length * subscription.charge, requiredSubscriptions = Math.ceil(requiredCredits / subscription.max);
                if (subscription.type === 'subscription' && subscription.buyable) {
                    return _jsx(PaymentOptionSubscriptionBuy, { subscription: subscription, addSubscription: true, invoiceTotal: subscription.sellPrice * requiredSubscriptions, addSubscriptions: requiredSubscriptions });
                }
                else if (subscription.type === 'membership' && subscription.buyable && variant === "full") {
                    return _jsx(PaymentOptionMembershipBuy, { subscription: subscription });
                }
                else if (subscription.type === 'membership' && subscription.mandatable) {
                    return _jsx(PaymentOptionMembershipMandate, { subscription: subscription });
                }
            }
        }
        return (_jsx(_Fragment, {}));
    };
    useEffect(() => {
        var _a;
        if (displayPaymentMethod === null || displayPaymentMethod === void 0 ? void 0 : displayPaymentMethod.startsWith('subscription_')) {
            let _subtotal = 0;
            let _rows = [];
            let appointmentProductLines = cart.lines.filter((line) => line.productType === 'appointment');
            if (appointmentProductLines.length === 0) {
                let appointment = undefined;
                if ((booking === null || booking === void 0 ? void 0 : booking.appointments) && (booking === null || booking === void 0 ? void 0 : booking.appointments.length) > 0) {
                    appointment = booking.appointments[0];
                    _rows.push(createRow([''], appointment.price, booking.appointments.length, 0, //   21,
                    "appointment", "appointment"));
                }
            }
            let firstAppointment = true;
            appointmentProductLines.map((line) => {
                var _a;
                // should always be 1 , for now 
                // nope, we have multiple now
                if (line.productType === 'appointment') {
                    let appointment = booking.appointments.find((appointment) => appointment.id === line.appointmentId);
                    if (!appointment) {
                        appointment = booking.appointments[0];
                    }
                    if (appointment) {
                        if (firstAppointment) {
                            _rows.push(createRow([''], (_a = line === null || line === void 0 ? void 0 : line.originalPrice) !== null && _a !== void 0 ? _a : line.price, booking.appointments.length, 0, //   21,
                            "appointment", "appointment"));
                            //}
                            firstAppointment = false;
                        }
                    }
                }
            });
            let otherProductLines = cart.lines.filter((line) => line.productType !== 'appointment');
            otherProductLines.map((line) => {
                var _a, _b, _c;
                _rows.push(createRow([
                    line.name
                ], (_a = line === null || line === void 0 ? void 0 : line.originalPrice) !== null && _a !== void 0 ? _a : line.price, (_b = line.quantity) !== null && _b !== void 0 ? _b : 1, 0, //   21,
                line.productType, (_c = line.origin) !== null && _c !== void 0 ? _c : "optionalPurchase"));
            });
            let subscription = (_a = consumer.subscriptions) === null || _a === void 0 ? void 0 : _a.find((subscription) => subscription.id === parseInt(displayPaymentMethod.substring(13)));
            if (subscription === null || subscription === void 0 ? void 0 : subscription.subscriptionId) {
                if (subscription.charge) {
                    let charge = subscription.charge * (booking === null || booking === void 0 ? void 0 : booking.appointments.length);
                    if (charge > subscription.balance) {
                        // todo: is a balance of -1 a thing?
                        let remainder = (charge - subscription.balance) / subscription.charge;
                        charge = subscription.balance;
                        setRequiresAdditionalPayment(true);
                        let _remainingRows = Array.from(_rows);
                        _remainingRows.map((row) => {
                            if (row.type === "appointment") {
                                row.qty = remainder;
                                row.price = row.unit * remainder;
                            }
                            return row;
                        });
                        _subtotal = subtotal(_remainingRows);
                        setInvoiceTotal(_subtotal);
                    }
                }
            }
        }
        else {
            setRequiresAdditionalPayment(false);
        }
    }, [displayPaymentMethod, cart.lines]);
    // 'Niet online afrekenen' belongs on this first step only when a subscription/strippenkaart or
    // not paying online at all is the entire choice. With any other method in the list it is one of
    // the options behind "toon overige", where the provider loop renders it.
    const onlyNoneAndAlternate = paymentMethods.length === 2
        && paymentMethods.includes('none')
        && paymentMethods.includes('alternate');
    return (_jsx(BoxContainer, { sx: {
            paddingTop: 2
        }, children: _jsxs(Box, { sx: {}, children: [paymentError && (_jsx(ErrorAlert, { messages: paymentError, setter: setPaymentError })), _jsx(ErrorAlert, { messages: errorValidate, setter: setErrorValidate }), _jsx(SuccessAlert, { messages: successValidate, setter: setSuccessValidate }), !loader && !isPaying && (_jsx(CartComponent, { canDeposit: canDeposit && showExternals, useDeposit: useDeposit, setter: setUseDeposit, fees: cartFees, deposit: depositPrice, locked: false, paymentMethod: displayPaymentMethod, canRedeem: hasCheckout && showExternals && !cart.lines.find((line) => ["subscription", "membership"].includes(line.productType)), setSubTotal: setSubTotal, setErrorValidate: setErrorValidate, setSuccessValidate: setSuccessValidate, errorValidate: errorValidate, successValidate: successValidate })), paymentMethods && paymentMethods.length === 0 && (_jsxs(Box, { sx: { width: '100%' }, children: [_jsx(Typography, { component: "p", variant: "body2", style: { margin: 2, textAlign: 'center' }, children: t('Ophalen van betaalmethodes...') }), _jsx(LinearProgress, {})] })), cart.order_id && isPaying && displayPaymentMethod !== "none" && !(displayPaymentMethod === null || displayPaymentMethod === void 0 ? void 0 : displayPaymentMethod.startsWith('membership')) && !(displayPaymentMethod === null || displayPaymentMethod === void 0 ? void 0 : displayPaymentMethod.startsWith('subscription')) && subTotal > 0 && (_jsxs(_Fragment, { children: [_jsx(Divider, {}), _jsx(PollingPaymentStatus, { method: displayPaymentMethod !== null && displayPaymentMethod !== void 0 ? displayPaymentMethod : 'ideal', paymentId: cart.order_id, transaction: {
                                redirectUrl: redirectUri
                            } }), _jsx(Button, { type: "button", fullWidth: true, sx: {
                                my: 4
                            }, variant: "contained", onClick: backCallback, children: t("Betaling afbreken", "payment") })] })), _jsx(FormContainer, { formContext: formContext, onSuccess: onSubmit, onError: onError, children: _jsxs(FormErrorProvider, { onError: (error) => {
                            if (error.type === 'validate') {
                                console.log(error);
                            }
                            return t(error.type, 'error');
                        }, children: [error && (_jsx(ErrorAlert, { messages: error, setter: setError })), paymentMethods && paymentMethods.length > 0 && !isPaying && (_jsxs(_Fragment, { children: [loader && (_jsxs(Box, { sx: { width: '100%' }, children: [_jsx(Typography, { component: "p", variant: "body2", style: { margin: 2, textAlign: 'center' }, children: t('Betaling wordt gestart...') }), _jsx(LinearProgress, {})] })), !loader && !updateCartQuery.isFetching && (_jsx(_Fragment, { children: cart.order_id && (_jsxs(_Fragment, { children: [requiresAdditionalPayment && !(paymentMethod === null || paymentMethod === void 0 ? void 0 : paymentMethod.endsWith('addPayment')) && !(paymentMethod === null || paymentMethod === void 0 ? void 0 : paymentMethod.endsWith('addSubscription')) && (_jsx(Alert, { sx: { my: 2, mx: 1 }, severity: "warning", children: _jsx(Typography, { children: ts('Let op: je gekozen strippenkaart heeft niet voldoende strippen om de %amount% %amount=1?afspraak:afspraken% te betalen', { amount: booking.appointments.length }) }) })), (!showExternals || (showExternals && subTotal > 0)) && (_jsx(Typography, { variant: "h6", sx: { marginLeft: 1, marginTop: 0, fontSize: '1rem' }, children: t('Hoe wil je betalen?') })), _jsx(RadioGroup, { name: "paymentMethod", onChange: onChange, children: _jsx(TableContainer, { component: Paper, children: _jsx(Table, { sx: { width: '100%' }, size: "small", "aria-label": "a dense table", children: _jsxs(TableBody, { children: [consumer.subscriptions && !cart.lines.find((line) => ['subscription', 'membership'].includes(line.productType)) && (_jsx(_Fragment, { children: consumer.subscriptions.map((subscription) => {
                                                                            if (showExternals) {
                                                                                // only show external payment providers
                                                                                return (_jsx(Fragment, {}, `${subscription.type}_${subscription.id}`));
                                                                            }
                                                                            if (subscription.type === 'subscription' && subscription.charge > subscription.balance && subscription.balance !== -1) {
                                                                                // not enough credits, probably already filtered on server
                                                                                return (_jsx(Fragment, {}, `${subscription.type}_${subscription.id}`));
                                                                            }
                                                                            if (showAlternates && !subscription.subscriptionId) {
                                                                                // don't show buy a subscription
                                                                                return (_jsx(Fragment, {}, `${subscription.type}_${subscription.id}`));
                                                                            }
                                                                            if (showMoreAlternates && subscription.subscriptionId) {
                                                                                // don't show use a subscription
                                                                                return (_jsx(Fragment, {}, `${subscription.type}_${subscription.id}`));
                                                                            }
                                                                            if (subscription.type === "subscription" && requiresAdditionalPayment) {
                                                                                return (_jsxs(Fragment, { children: [subscription.sellPrice > 0 && (_jsxs(TableRow, { sx: {
                                                                                                backgroundColor: theme.palette.background.default,
                                                                                                '&:last-child td, &:last-child th': { border: 0 }
                                                                                            }, children: [_jsx(TableCell, { component: "th", scope: "row", colSpan: subscription.subscriptionId ? 2 : 1, children: _jsx(FormControlLabel, { value: `${subscription.type}_${subscription.id}_addSubscription`, control: _jsx(Radio, {}), label: _jsx(PaymentOption, { subscription: subscription, variant: "addSubscription" }) }) }), !subscription.subscriptionId && (_jsx(StyledTableCell, { children: subscription.sellPrice > 0 && (_jsx(Chip, { variant: 'filled', component: 'span', label: _jsx(_Fragment, { children: moneyFormat(subscription.sellPrice) }) })) }))] }, `${subscription.type}_${subscription.id}_addSubscription`)), _jsxs(TableRow, { sx: {
                                                                                                backgroundColor: theme.palette.background.default,
                                                                                                '&:last-child td, &:last-child th': { border: 0 }
                                                                                            }, children: [_jsx(TableCell, { component: "th", scope: "row", colSpan: subscription.subscriptionId ? 2 : 1, children: _jsx(FormControlLabel, { value: `${subscription.type}_${subscription.id}_addPayment`, control: _jsx(Radio, {}), label: _jsx(PaymentOption, { subscription: subscription, variant: "addPayment" }) }) }), !subscription.subscriptionId && (_jsx(StyledTableCell, { children: subscription.sellPrice > 0 && (_jsx(Chip, { variant: 'filled', component: 'span', label: _jsx(_Fragment, { children: moneyFormat(subscription.sellPrice) }) })) }))] }, `${subscription.type}_${subscription.id}_addPayment`)] }, `${subscription.type}_${subscription.id}`));
                                                                            }
                                                                            return (_jsx(Fragment, { children: _jsxs(TableRow, { sx: {
                                                                                        backgroundColor: theme.palette.background.default,
                                                                                        '&:last-child td, &:last-child th': { border: 0 }
                                                                                    }, children: [_jsx(TableCell, { component: "th", scope: "row", colSpan: subscription.subscriptionId ? 2 : 1, children: _jsx(FormControlLabel, { value: `${subscription.type}_${subscription.id}`, control: _jsx(Radio, {}), label: _jsx(PaymentOption, { subscription: subscription, variant: "full" }) }) }), !subscription.subscriptionId && (_jsx(StyledTableCell, { children: subscription.sellPrice > 0 && (_jsx(Chip, { variant: 'filled', component: 'span', label: _jsx(_Fragment, { children: moneyFormat(subscription.sellPrice) }) })) }))] }, `${subscription.type}_${subscription.id}`) }, `${subscription.type}_${subscription.id}`));
                                                                        }) })), hasValidMoreAlternates && !showMoreAlternates && !showExternals && (_jsx(TableRow, { sx: {
                                                                            backgroundColor: theme.palette.background.default,
                                                                            '&:last-child td, &:last-child th': { border: 0 }
                                                                        }, children: _jsx(TableCell, { component: "th", scope: "row", colSpan: 2, children: _jsx(FormControlLabel, { value: 'alternate', control: _jsx(Radio, {}), label: _jsx(PaymentOption, { provider: {
                                                                                        key: 'alternate',
                                                                                        icon: '',
                                                                                        label: 'Toon overige abonnementen en strippenkaarten'
                                                                                    } }) }) }) }, 'alternate')), onlyNoneAndAlternate && (_jsx(TableRow, { sx: {
                                                                            backgroundColor: theme.palette.background.default,
                                                                            '&:last-child td, &:last-child th': { border: 0 }
                                                                        }, children: _jsx(TableCell, { component: "th", scope: "row", colSpan: 2, children: _jsx(FormControlLabel, { value: 'none', control: _jsx(Radio, {}), label: _jsx(PaymentOption, { provider: {
                                                                                        key: 'none',
                                                                                        icon: '',
                                                                                        label: 'Niet online afrekenen'
                                                                                    } }) }) }) }, 'none')), hasValidExternals && !showExternals && (!showMoreAlternates || (showMoreAlternates && !hasValidUsables)) && (_jsx(TableRow, { sx: {
                                                                            backgroundColor: theme.palette.background.default,
                                                                            '&:last-child td, &:last-child th': { border: 0 }
                                                                        }, children: _jsx(TableCell, { component: "th", scope: "row", colSpan: 2, children: _jsx(FormControlLabel, { value: 'external', control: _jsx(Radio, {}), label: _jsx(PaymentOption, { provider: {
                                                                                        key: 'external',
                                                                                        icon: '',
                                                                                        label: 'Losse afspraak maken'
                                                                                    } }) }) }) }, 'external')), hasValidExternals && showExternals && (subTotal > 0 || displayPaymentMethod === "none") && (_jsx(_Fragment, { children: payment.providers.map((provider) => {
                                                                            if (!paymentMethods.includes(provider.key) || provider.key === 'directdebit') {
                                                                                return (_jsx(Fragment, {}, provider.key));
                                                                            }
                                                                            if (provider.key.startsWith('subscription_') || provider.key.startsWith('membership_')) {
                                                                                return (_jsx(Fragment, {}, provider.key));
                                                                            }
                                                                            if (subTotal === 0 && paymentMethod !== "none") {
                                                                                // nothing left to pay
                                                                                return (_jsx(Fragment, {}, provider.key));
                                                                            }
                                                                            return (_jsxs(TableRow, { sx: {
                                                                                    backgroundColor: theme.palette.background.default,
                                                                                    '&:last-child td, &:last-child th': { border: 0 }
                                                                                }, children: [_jsx(TableCell, { component: "th", scope: "row", colSpan: provider.key === 'none' ? 1 : 2, children: _jsx(FormControlLabel, { value: provider.key, control: _jsx(Radio, {}), label: _jsx(PaymentOption, { provider: provider }) }) }), provider.key !== 'none' && (_jsx(StyledTableCell, { children: provider.icon && (_jsx("img", { src: provider.icon, style: {
                                                                                                height: '3em',
                                                                                                aspectRatio: '4 / 3',
                                                                                                boxSizing: 'border-box'
                                                                                            } })) }))] }, provider.key));
                                                                        }) }))] }) }) }) })] })) }))] }))] }) }), updateCartQuery.isFetching && (_jsxs(Box, { sx: { width: '100%' }, children: [_jsx(Typography, { component: "p", variant: "body2", style: { marginTop: 4, marginBottom: 2, textAlign: 'center' }, children: t('De betaalmethode wordt bijgewerkt...') }), _jsx(LinearProgress, {})] })), !loader && !isPaying && (_jsxs("form", { action: submitUrl.href, target: '_blank', method: "POST", onSubmit: prepareSubmit, children: [_jsx("input", { type: "hidden", id: "payload", name: "payload" }), _jsx("input", { type: "hidden", id: "csrfToken", name: "token" }), _jsx("input", { type: "hidden", id: "secureToken", name: "secureToken" }), _jsx(Box, { sx: {}, children: _jsx(Button, { type: "submit", fullWidth: true, sx: {
                                    my: 4
                                }, variant: "contained", disabled: !enableSubmit || updateCartQuery.isFetching || abortQuery.isFetching, children: t(submitLabel, "payment") }) })] }))] }) }));
}
